import {
  bucketName_files,
  documentBackupBucketName,
  FRONTEND_STORAGE_JSON_MAX_MB,
} from "../components/env";

/** Limit JSON payloads loaded from `files` / `document-backups` in the browser (e.g. EditableDocumentPage). */
export const FRONTEND_STORAGE_JSON_MAX_BYTES =
  FRONTEND_STORAGE_JSON_MAX_MB * 1024 * 1024;

export function isProbablyJsonStoragePath(path) {
  if (!path || typeof path !== "string") return false;
  return path.toLowerCase().endsWith(".json");
}

export function isRestrictedJsonStorageBucket(bucketName) {
  return (
    bucketName === bucketName_files || bucketName === documentBackupBucketName
  );
}

export function formatStorageJsonTooLargeMessage(sizeBytes) {
  const actualMb = sizeBytes / (1024 * 1024);
  return `This file is about ${actualMb.toFixed(1)} MB. JSON from storage is limited to ${FRONTEND_STORAGE_JSON_MAX_MB} MB in the app.`;
}

/**
 * @param {Blob | null | undefined} blob
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function checkFrontendStorageJsonBlobSize(blob) {
  if (!blob || typeof blob.size !== "number") return { ok: true };
  if (blob.size > FRONTEND_STORAGE_JSON_MAX_BYTES) {
    return {
      ok: false,
      message: formatStorageJsonTooLargeMessage(blob.size),
    };
  }
  return { ok: true };
}

/**
 * Lightweight metadata fetch (no object body) via Storage API `info`.
 * @returns {Promise<
 *   | { status: 'skip' }
 *   | { status: 'ok'; sizeBytes: number }
 *   | { status: 'too_large'; sizeBytes: number; message: string }
 *   | { status: 'not_found' }
 *   | { status: 'unknown'; error?: unknown }
 * >}
 */
export async function preflightRestrictedStorageJsonSize(
  supabase,
  bucketName,
  filePath,
) {
  if (!isRestrictedJsonStorageBucket(bucketName)) {
    return { status: "skip" };
  }
  if (!isProbablyJsonStoragePath(filePath)) {
    return { status: "skip" };
  }

  const cleanPath = String(filePath || "").replace(/^\/+/, "");
  if (!cleanPath) return { status: "unknown" };

  try {
    const { data, error } = await supabase.storage
      .from(bucketName)
      .info(cleanPath);

    if (error) {
      const msg = String(error.message || "").toLowerCase();
      const code = error.statusCode ?? error.status;
      const notFound =
        code === 400 ||
        code === 404 ||
        msg.includes("not found") ||
        msg.includes("object not found") ||
        msg.includes("404") ||
        msg.includes("bad request");
      if (notFound) return { status: "not_found" };
      return { status: "unknown", error };
    }

    const sizeBytes = typeof data?.size === "number" ? data.size : null;
    if (sizeBytes == null) {
      return { status: "unknown", error: new Error("missing size in info()") };
    }
    if (sizeBytes > FRONTEND_STORAGE_JSON_MAX_BYTES) {
      return {
        status: "too_large",
        sizeBytes,
        message: formatStorageJsonTooLargeMessage(sizeBytes),
      };
    }
    return { status: "ok", sizeBytes };
  } catch (e) {
    return { status: "unknown", error: e };
  }
}
